// Elintra — UAG API Client | v3.3 | Constitutional Commerce
// Frontend types MIRROR the backend's real (now-LIVE) response shapes (Arc
// authority order: backend -> frontend types). Each fetcher maps the backend
// JSON (snake_case) onto the dashboard type and returns the LIVE values on
// success. Fixtures are used ONLY (a) in fixtures-mode (VITE_USE_FIXTURES==='true'
// or no base URL — Canvas / no-env builds) and (b) as an ERROR-fallback: a failed
// fetch THROWS, and the caller's Promise.allSettled keeps the seeded fixtures so a
// backend outage doesn't blank the dashboard. /stores is genuinely empty (live []),
// so on success it returns [] and the UI shows a clean empty state — NOT fixtures.
// import.meta cast pattern preserves Vite env injection.

import type {
  AgentQuery, ProtocolHealthItem, StoreRecord,
  ChartDataPoint, PaymentRailHealthItem, WsStatus,
  Protocol, QueryOutcome, SettlementRail, RailStatus,
} from '@/types/app';
import {
  PROTOCOL_HEALTH_FALLBACK, PAYMENT_RAILS_FALLBACK,
  STORE_FIXTURES, generateChartData,
} from './fixtures';

const _env        = (import.meta as unknown as { env?: Record<string, string> }).env ?? {};
const UAG_BASE    = _env.VITE_UAG_BASE_URL ?? '';
const UAG_WS      = _env.VITE_UAG_WS_URL   ?? '';
const USE_FIXTURES = _env.VITE_USE_FIXTURES === 'true' || UAG_BASE === '';

// Fixtures-mode is true when VITE_USE_FIXTURES==='true' or no base URL is set.
// Against a LIVE backend this is false — used to gate the offline mock-seed so
// the real fetched queries (not synthetic mocks) are what render.
export const isFixturesMode = (): boolean => USE_FIXTURES;

export class UagError extends Error {
  constructor(msg: string, public status?: number, public endpoint?: string) {
    super(msg); this.name = 'UagError';
  }
}

async function authedFetch<T>(
  path: string, sessionToken: string, options?: RequestInit,
): Promise<T> {
  const res = await fetch(`${UAG_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type':       'application/json',
      'X-Elintra-Session':  sessionToken,
      ...options?.headers,
    },
  });
  if (!res.ok) throw new UagError(`UAG ${res.status}: ${res.statusText}`, res.status, path);
  return res.json() as Promise<T>;
}

// ─── /health ─────────────────────────────────────────────────────────────────
// Backend HealthResponse (health.rs) now serves the live counters:
// { status, service, implements_uag, implementation_version, build_sha,
//   intent_graph_size: number, x402_payment_count: number,
//   algo_settled_usdt: decimal STRING }. We map them straight onto the store
// (intentGraphSize / x402PaymentCount / algoSettledUsdt). On a failed fetch we
// fall back to fixtures so a backend outage does not blank the dashboard.

interface UagHealthBackend {
  status:                  string;
  service:                 string;
  implements_uag:          string;
  implementation_version:  string;
  build_sha:               string;
  intent_graph_size?:      number;
  x402_payment_count?:     number;
  algo_settled_usdt?:      string;   // decimal STRING — never parsed through a float
}

export interface UagHealthResponse {
  status:           string;
  version:          string;          // mapped from implementation_version
  // Live counters are OPTIONAL: the staging edge /health currently serves only
  // { status, service }, so these are typically absent here and the store derives
  // them from the live /analytics/chart series (the canonical source). When a
  // future backend adds them to /health, they are consumed directly.
  intentGraphSize:  number | null;   // live: intent_graph_size
  x402PaymentCount: number | null;   // live: x402_payment_count
  algoSettledUsdt:  string | null;   // live: algo_settled_usdt (decimal STRING)
}

export const HEALTH_FALLBACK = {
  intentGraphSize:  142_589,
  x402PaymentCount: 0,
  algoSettledUsdt:  '0.00',
} as const;

export async function uagFetchHealth(): Promise<UagHealthResponse> {
  if (USE_FIXTURES) {
    return { status: 'ok', version: '3.3.0', ...HEALTH_FALLBACK };
  }
  const res = await fetch(`${UAG_BASE}/health`);
  if (!res.ok) throw new UagError('Health failed', res.status, '/health');
  const body = await res.json() as UagHealthBackend;
  return {
    status:           body.status ?? 'ok',
    version:          body.implementation_version ?? '3.3.0',
    // null when the edge omits them → the store derives from the live chart.
    intentGraphSize:  body.intent_graph_size  ?? null,
    x402PaymentCount: body.x402_payment_count ?? null,
    // decimal STRING — pass through verbatim, never parse through a float
    algoSettledUsdt:  body.algo_settled_usdt  ?? null,
  };
}

// ─── /agent-queries ───────────────────────────────────────────────────────────
// Backend AgentQueriesResponse now serves enriched rows:
// { queries: [{ query_id, agent_id_hex, protocol, outcome, settlement_tx_id?,
//   governance_hash, created_at, query_intent?, product_matched?, amount?,
//   latency_ms?, settlement_rail? }] } — snake_case. Pre-enrichment rows have
// query_intent / product_matched / amount / latency_ms / settlement_rail null;
// we map them gracefully (blanks, never crash). amount is a decimal STRING.

interface AgentQueryRecordBackend {
  query_id:          string;
  agent_id_hex:      string;
  protocol:          string;
  outcome:           string;
  settlement_tx_id?: string | null;
  governance_hash:   string;
  created_at:        string;
  query_intent?:     string | null;
  product_matched?:  string | null;
  amount?:           string | null;   // decimal STRING — never parsed through a float
  latency_ms?:       number | null;
  settlement_rail?:  string | null;   // e.g. 'algorand_x402'
}

interface AgentQueriesResponseBackend {
  queries: AgentQueryRecordBackend[];
}

const KNOWN_PROTOCOLS: readonly Protocol[] = ['A2A', 'ACP', 'UCP', 'NLIP'];
const KNOWN_OUTCOMES:  readonly QueryOutcome[] = ['PURCHASED', 'MATCHED', 'REJECTED'];

function asProtocol(wire: string): Protocol {
  // Backend may emit X402 (payment-layer); the routing-protocol AgentQuery type
  // only models A2A/ACP/UCP/NLIP, so non-routing wires fall back to A2A.
  return KNOWN_PROTOCOLS.includes(wire as Protocol) ? (wire as Protocol) : 'A2A';
}

function asOutcome(wire: string): QueryOutcome {
  return KNOWN_OUTCOMES.includes(wire as QueryOutcome) ? (wire as QueryOutcome) : 'REJECTED';
}

function mapAgentQuery(r: AgentQueryRecordBackend): AgentQuery {
  return {
    id:             r.query_id,
    agentId:        r.agent_id_hex,
    protocol:       asProtocol(r.protocol),
    queryIntent:    r.query_intent ?? '',          // null on pre-enrichment rows → blank
    productMatched: r.product_matched ?? null,
    outcome:        asOutcome(r.outcome),
    amount:         r.amount ?? null,              // decimal STRING (or null)
    latencyMs:      r.latency_ms ?? null,
    timestamp:      r.created_at,
    settlementRail: r.settlement_rail ?? undefined,
    governanceHash: r.governance_hash || undefined,
    algorand_tx_id: r.settlement_tx_id ?? undefined,
  };
}

export async function uagFetchAgentQueries(token: string): Promise<AgentQuery[]> {
  if (USE_FIXTURES) return [];
  const body = await authedFetch<AgentQueriesResponseBackend>('/agent-queries', token);
  return (body.queries ?? []).map(mapAgentQuery);
}

// ─── /protocol-health ───────────────────────────────────────────────────────
// Backend ProtocolHealthResponse now serves the rich fields directly:
// { protocols: [{ id, label, owner, endpoint, type: 'ROUTING'|'PAYMENT_LAYER',
//   status }] }. We consume them straight (no fixture-merge). The UI maps status
// strings ('Healthy'/'Active'/…) to badge variants and reads `type` to split
// routing vs payment-layer; titleCaseStatus normalizes a lower-case wire status.

function titleCaseStatus(s: string): string {
  // Backend may emit a lower-case wire status ("operational"/"active"); the UI
  // compares Title-case ("Healthy"/"Active"/"Preview"/"Degraded"). Map the known
  // backend value, else Title-case the first letter, else pass through.
  if (s === 'operational') return 'Healthy';
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

type ProtocolType = 'ROUTING' | 'ROUTING_PROTOCOL' | 'PAYMENT_LAYER';

function asProtocolType(t: string | undefined): ProtocolType | undefined {
  if (t === 'PAYMENT_LAYER') return 'PAYMENT_LAYER';
  if (t === 'ROUTING' || t === 'ROUTING_PROTOCOL') return t;
  return undefined;
}

interface ProtocolHealthItemBackend {
  id:        string;
  label:     string;
  owner:     string;
  endpoint:  string;
  type:      string;       // 'ROUTING' | 'PAYMENT_LAYER'
  status:    string;
}

interface ProtocolHealthResponseBackend {
  protocols: ProtocolHealthItemBackend[];
}

function mapProtocolHealth(item: ProtocolHealthItemBackend): ProtocolHealthItem {
  return {
    id:       item.id,
    label:    item.label,
    owner:    item.owner,
    endpoint: item.endpoint,
    status:   titleCaseStatus(item.status),
    type:     asProtocolType(item.type),
  };
}

export async function uagFetchProtocolHealth(token: string): Promise<ProtocolHealthItem[]> {
  if (USE_FIXTURES) return PROTOCOL_HEALTH_FALLBACK;
  const body = await authedFetch<ProtocolHealthResponseBackend>('/protocol-health', token);
  if (!body.protocols || body.protocols.length === 0) return PROTOCOL_HEALTH_FALLBACK;
  return body.protocols.map(mapProtocolHealth);
}

// ─── /stores ──────────────────────────────────────────────────────────────────
// Backend StoresResponse (stores.rs): { stores: [{ store_id, display_name,
// segment }] } — genuinely EMPTY (Phase-2C, no store onboarding yet). Against a
// live backend an empty list is the TRUTH: we return [] and the UI shows a clean
// empty state ("no stores connected yet"). Only a FAILED fetch falls back to
// fixtures (so an outage doesn't blank the dashboard) — that path throws here and
// the store keeps its seeded fixtures. In fixtures mode we serve the showcase set.

interface StoreItemBackend {
  store_id:     string;
  display_name: string;
  segment:      string;
}

interface StoresResponseBackend {
  stores: StoreItemBackend[];
}

function mapStore(s: StoreItemBackend): StoreRecord {
  return {
    id:           s.store_id,
    platform:     s.segment,           // segment is the closest real field
    name:         s.display_name,
    status:       'connected',         // no live status field yet
    productCount: 0,                   // not supplied yet
    lastSync:     new Date().toISOString(), // not supplied yet
  };
}

export async function uagFetchStores(token: string): Promise<StoreRecord[]> {
  if (USE_FIXTURES) return STORE_FIXTURES;
  const body = await authedFetch<StoresResponseBackend>('/stores', token);
  // Empty list is the live TRUTH (Phase-2C) → [] drives the empty state.
  return (body.stores ?? []).map(mapStore);
}

// ─── /analytics/chart ─────────────────────────────────────────────────────────
// Backend ChartResponse (analytics.rs) is now platform-wide (no creator_id
// required) and serves the full series the dashboard renders:
// { days, series: [{ date, A2A, ACP, UCP, NLIP, queries, conversions,
//   x402_payments, algo_volume_usdt: decimal STRING }] }. We map it straight onto
// ChartDataPoint[] (algo_volume_usdt is kept as a decimal STRING, never parsed
// through a float). On a failed fetch we fall back to the generated fixture chart.

interface ChartSeriesPointBackend {
  date:              string;          // YYYY-MM-DD
  A2A:               number;
  ACP:               number;
  UCP:               number;
  NLIP:              number;
  queries:           number;
  conversions:       number;
  x402_payments:     number;
  algo_volume_usdt:  string;          // decimal STRING — never parsed through a float
}

interface ChartResponseBackend {
  days:   number;
  series: ChartSeriesPointBackend[];
}

function isoDayToLabel(day: string): string {
  // Backend date is "YYYY-MM-DD"; the chart x-axis uses "Mon D" (en-US short).
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? day
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function mapChartSeries(series: ChartSeriesPointBackend[]): ChartDataPoint[] {
  return series.map((row): ChartDataPoint => ({
    date:             isoDayToLabel(row.date),
    A2A:              row.A2A         ?? 0,
    ACP:              row.ACP         ?? 0,
    UCP:              row.UCP         ?? 0,
    NLIP:             row.NLIP        ?? 0,
    queries:          row.queries     ?? 0,
    conversions:      row.conversions ?? 0,
    x402_payments:    row.x402_payments ?? 0,
    // decimal STRING preserved verbatim (UI sums these via integer-cents math)
    algo_volume_usdt: row.algo_volume_usdt ?? '0',
  }));
}

export async function uagFetchAnalyticsChart(
  token: string, _creatorId?: string, days = 30,
): Promise<ChartDataPoint[]> {
  // Platform-wide: no creator_id needed. The chart is live; only a failed fetch
  // (handled by the caller's allSettled) falls back to the generated chart.
  if (USE_FIXTURES) return generateChartData();
  const body = await authedFetch<ChartResponseBackend>(
    `/analytics/chart?days=${days}`, token,
  );
  if (!body.series || body.series.length === 0) return generateChartData();
  return mapChartSeries(body.series);
}

// ─── /payment-rails ───────────────────────────────────────────────────────────
// Backend PaymentRailsResponse now serves the rich fields directly:
// { rails: [{ id, status, address?, phase, label, network?, facilitator?,
//   finalityMs? }] } — note label/network/facilitator/finalityMs are now backend-
// supplied. We consume them straight (no fixture-merge). On a failed fetch the
// caller's allSettled keeps the seeded fixtures so an outage doesn't blank the UI.

interface PaymentRailItemBackend {
  id:           string;
  status:       string;
  address?:     string | null;
  phase:        string;
  label:        string;
  network?:     string | null;
  facilitator?: string | null;
  finalityMs?:  number | null;
}

interface PaymentRailsResponseBackend {
  rails: PaymentRailItemBackend[];
}

const KNOWN_RAIL_STATUS: readonly RailStatus[] = ['active', 'ready', 'preview', 'degraded'];

function asRailStatus(s: string): RailStatus {
  return KNOWN_RAIL_STATUS.includes(s as RailStatus) ? (s as RailStatus) : 'preview';
}

function mapPaymentRail(r: PaymentRailItemBackend): PaymentRailHealthItem {
  return {
    id:          r.id as SettlementRail,
    label:       r.label,
    status:      asRailStatus(r.status),
    address:     r.address ?? undefined,
    phase:       r.phase,
    network:     r.network ?? undefined,
    facilitator: r.facilitator ?? undefined,
    finalityMs:  r.finalityMs ?? undefined,
  };
}

export async function uagFetchPaymentRails(token: string): Promise<PaymentRailHealthItem[]> {
  if (USE_FIXTURES) return PAYMENT_RAILS_FALLBACK;
  const body = await authedFetch<PaymentRailsResponseBackend>('/payment-rails', token);
  if (!body.rails || body.rails.length === 0) return PAYMENT_RAILS_FALLBACK;
  return body.rails.map(mapPaymentRail);
}

// ─── WebSocket live feed ───────────────────────────────────────────────────────
// Dual-auth preserved: browsers cannot set custom headers on the WS handshake,
// so the session is passed as the ?session= query param (the backend ws_feed
// handler reads X-Elintra-Session header first, then the query param).

export interface FeedCallbacks {
  onQuery:        (q: AgentQuery) => void;
  onStatusChange: (s: WsStatus)   => void;
  onError:        (e: Error)      => void;
}

export interface FeedConnection { close: () => void; getStatus: () => WsStatus }

export function uagCreateFeedConnection(
  userId: string, sessionToken: string, callbacks: FeedCallbacks,
): FeedConnection {
  if (USE_FIXTURES || UAG_WS === '') {
    callbacks.onStatusChange('disconnected');
    return { close: () => {}, getStatus: () => 'disconnected' };
  }

  let ws:         WebSocket | null = null;
  let status:     WsStatus         = 'connecting';
  let retryCount  = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let closed      = false;

  const setStatus = (s: WsStatus) => { status = s; callbacks.onStatusChange(s); };

  const connect = () => {
    if (closed) return;
    ws = new WebSocket(`${UAG_WS}/agents/${userId}?session=${sessionToken}`);
    setStatus('connecting');
    ws.onopen    = () => { retryCount = 0; setStatus('connected'); };
    ws.onmessage = (e: MessageEvent) => {
      try { callbacks.onQuery(JSON.parse(e.data as string) as AgentQuery); }
      catch { callbacks.onError(new Error('Failed to parse WS message')); }
    };
    ws.onerror   = () => { callbacks.onError(new Error('WS error')); setStatus('error'); };
    ws.onclose   = () => {
      if (closed) return;
      setStatus('disconnected');
      if (retryCount < 10) {
        const delay = Math.min(1_000 * Math.pow(2, retryCount), 30_000);
        retryCount++;
        retryTimer = setTimeout(connect, delay);
      }
    };
  };

  connect();
  return {
    close: () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
      setStatus('disconnected');
    },
    getStatus: () => status,
  };
}
